import { CdrFilters } from '@reliance/types/filters/CdrFilters';
import { Cdr } from '@reliance/types/models/Cdr';
import { PaginatedResponse } from '@reliance/types/responses/PaginatedResponse';
import React, { useMemo } from 'react';
import { useAsync } from 'react-async-hook';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import CdrTable, {
  CDR_TABLE_SERVER_COLUMNS,
  CdrTableColumnId,
} from './Table';
import api from '../common/api';
import { useServerTableFilterState } from '../table/useServerTableFilterState';

export type { CdrTableColumnId as CdrsModalColumnId };

const DEFAULT_PER_PAGE = 500;
const DEFAULT_ORDER_BY = '-setupDate';

type CdrsModalProps = {
  show: boolean;
  title: string;
  filters: CdrFilters;
  onHide: () => void;
  /** When set, only these columns are shown. Takes precedence over hiddenColumns. */
  visibleColumns?: CdrTableColumnId[];
  /** When set (and visibleColumns is not), these columns are hidden. */
  hiddenColumns?: CdrTableColumnId[];
};

const CdrsModal: React.FC<CdrsModalProps> = (props) => {
  const { show, title, filters: baseFilters, onHide, visibleColumns, hiddenColumns } = props;

  const baseRecord = useMemo(
    () =>
      ({
        orderBy: baseFilters.orderBy ?? DEFAULT_ORDER_BY,
        ...baseFilters,
      }) as Record<string, unknown>,
    [baseFilters]
  );

  const tableState = useServerTableFilterState(CDR_TABLE_SERVER_COLUMNS, baseRecord, {
    initialPagination: {
      pageIndex: 0,
      pageSize: baseFilters.perPage ?? DEFAULT_PER_PAGE,
    },
    initialSorting: [{ id: 'setupDate', desc: true }],
  });

  const filters = useMemo((): CdrFilters => {
    const merged = tableState.mergedRecord;
    return {
      ...baseFilters,
      page: merged.page as number,
      perPage: (merged.perPage as number) ?? DEFAULT_PER_PAGE,
      orderBy: (merged.orderBy as string | undefined) ?? DEFAULT_ORDER_BY,
    };
  }, [baseFilters, tableState.mergedRecord]);

  const cdrsResult = useAsync<PaginatedResponse<Cdr> | null>(
    () => {
      if (!show) {
        return Promise.resolve(null);
      }
      return api.get<PaginatedResponse<Cdr>>('/cdr', { params: filters }).then((r) => r.data);
    },
    [show, filters]
  );

  const cdrData = cdrsResult.result?.data ?? [];
  const total = cdrsResult.result?.total ?? 0;
  const perPage = cdrsResult.result?.perPage ?? tableState.pagination.pageSize;
  const totalPages = Math.ceil(total / perPage) || 1;
  const page = cdrsResult.result?.page ?? tableState.pagination.pageIndex + 1;

  return (
    <Modal show={show} onHide={onHide} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <CdrTable
          rows={cdrData}
          visibleColumns={visibleColumns}
          hiddenColumns={hiddenColumns}
          page={page}
          totalPages={totalPages}
          total={total}
          perPage={perPage}
          serverPagination={tableState.serverPagination}
          onServerPaginationChange={tableState.onServerPaginationChange}
          serverSorting={tableState.serverSorting}
          onServerSortingChange={tableState.onServerSortingChange}
          serverColumnFilters={tableState.serverColumnFilters}
          onServerColumnFiltersChange={tableState.onServerColumnFiltersChange}
          paginationMode="server"
          loading={show && cdrsResult.loading}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CdrsModal;
